import React from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from 'recharts';
import { formatCOP, formatCOPShort, formatNumber } from '../utils/kpiFormatters';
import type { KpiTrendPoint } from '../types';

interface Props {
  data: KpiTrendPoint[];
  isComparing: boolean;
  metricType?: 'revenue' | 'orders';
}

export const TrendAreaChart: React.FC<Props> = ({
  data,
  isComparing,
  metricType = 'revenue',
}) => {
  const isRevenue = metricType === 'revenue';

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const currVal = isRevenue ? payload[0]?.value : payload[0]?.payload?.current_orders;
    const prevVal = isRevenue ? payload[1]?.value : payload[1]?.payload?.previous_orders;

    let diffText = '';
    if (prevVal !== undefined && prevVal > 0) {
      const diffPct = (((currVal - prevVal) / prevVal) * 100).toFixed(1);
      diffText = `${Number(diffPct) >= 0 ? '+' : ''}${diffPct}%`;
    }

    return (
      <div className="bg-neutral-900 text-white p-3 rounded-xl shadow-lg border border-neutral-800 text-xs">
        <p className="font-bold text-neutral-300 mb-1.5">{label}</p>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Periodo actual:
            </span>
            <span className="font-bold">
              {isRevenue ? formatCOP(currVal) : `${formatNumber(currVal)} pedidos`}
            </span>
          </div>

          {isComparing && prevVal !== undefined && (
            <div className="flex items-center justify-between gap-4 text-neutral-400">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-neutral-400" />
                Periodo anterior:
              </span>
              <span>
                {isRevenue ? formatCOP(prevVal) : `${formatNumber(prevVal)} pedidos`}
              </span>
            </div>
          )}

          {isComparing && diffText && (
            <div className="pt-1 mt-1 border-t border-neutral-800 flex items-center justify-between">
              <span className="text-neutral-400">Variación:</span>
              <span className={`font-bold ${diffText.startsWith('+') ? 'text-emerald-400' : 'text-rose-400'}`}>
                {diffText}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="kpiTrendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#059669" stopOpacity={0.0} />
            </linearGradient>
            <linearGradient id="kpiPrevGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#94A3B8" stopOpacity={0.0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.5} />

          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: '#CBD5E1' }}
            tick={{ fontSize: 11, fill: '#64748B' }}
          />

          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => (isRevenue ? formatCOPShort(v) : formatNumber(v))}
            tick={{ fontSize: 11, fill: '#64748B' }}
          />

          <Tooltip content={<CustomTooltip />} />

          {isComparing && (
            <Area
              type="monotone"
              dataKey={isRevenue ? 'previous' : 'previous_orders'}
              name="Periodo anterior"
              stroke="#94A3B8"
              strokeWidth={2}
              strokeDasharray="4 4"
              fill="url(#kpiPrevGrad)"
            />
          )}

          <Area
            type="monotone"
            dataKey={isRevenue ? 'current' : 'current_orders'}
            name="Periodo actual"
            stroke="#059669"
            strokeWidth={2.5}
            fill="url(#kpiTrendGrad)"
            activeDot={{ r: 5, fill: '#059669', stroke: '#fff', strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
